import { useCallback, useEffect, useRef, useState } from "react";
import { Typography, Box, Avatar, Button, InputBase, IconButton } from "@mui/material";
import PenPaperIcon from "../../../../components/icons/PenPaperIcon";
import { FrontendStatus } from "../../../../types/bookings";
import { BackendStatus } from "../../../bookings/types/list-bookings";
import formatDate from "../../../../utils/FormatDate";
import { logout } from "../../../../store/slice/AuthSlice";
import apiService from "../../../../service/Api";
import { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { IStreams } from "../../../../types/streams";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import {
  FilterListOutlined,
  SearchOutlined,
  Key,
  KeyOffOutlined,
} from "@mui/icons-material";
import { DataGrid } from "../../../../components/common/Index";
import { GridRowParams } from "@mui/x-data-grid";
import SelectStreamModal from "./SelectStreamModal";
import { showErrorToast } from "../../../../components/common/ShowToast";

export interface Producer {
  name: string;
  avatar: string;
}

export interface IStream {
  vimeo_download_password: string | null;
  vimeo_stream_link: string | null;
  over_due_by: string | null;
  id: number;
  bookingId: string;
  title: string;
  clientName: string;
  serviceDate: string;
  serviceAddress: string;
  booking?: {
    status: string;
  };
  status: string;
  producer: string;
  start: string;
  end: string;
}

const statusMapToFrontend: Record<BackendStatus, FrontendStatus> = {
  pending: "enquiry",
  confirmed: "confirmed",
  completed: "fulfilled",
  cancelled: "cancelled",
  lost: "lost",
  testing: "testing",
};

interface StreamResponse {
  id: number;
  booking: { id: number; status: string };
  service: { venue: { name: string } };
  title: string;
  date: string;
  address_one: string;
  address_two: string;
  suburb: string;
  city: string;
  start: string;
  end: string;
  producer: string;
  vimeo_stream_link?: string;
  vimeo_download_password?: string;
}

export const StreamRow = () => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [streams, setStreams] = useState<IStreams[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStreamId, setSelectedStreamId] = useState<number | "">("");

  const fetchStreams = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        search: appliedSearchTerm,
        ordering: "-id",
      };

      const response = await apiService.get<{
        count: number;
        next: string | null;
        previous: string | null;
        results: StreamResponse[];
      }>("/api/streams/stream/", params);

      const validStatuses = [
        "pending",
        "confirmed",
        "completed",
        "cancelled",
        "lost",
        "testing",
      ];

      const mappedStreams = response.data.results.map((stream) => {
        const status: FrontendStatus = validStatuses.includes(
          stream.booking.status
        )
          ? statusMapToFrontend[stream.booking.status as BackendStatus] ||
            "enquiry"
          : "enquiry";

        return {
          id: stream.id.toString(),
          bookingId: `B${stream.booking.id.toString().padStart(3, "0")}`,
          title: stream.title || "Untitled",
          clientName: "Unknown",
          serviceDate: formatDate(stream.date),
          producer: stream.producer || "",
          dateOfService: stream.date,
          serviceAddress: `${stream?.service?.venue?.name || ""},${
            stream.address_one || ""
          }, ${stream.address_two || ""}, ${stream.suburb || ""}, ${
            stream.city || ""
          }`
            .replace(/, ,/g, ",")
            .replace(/,,/g, ",")
            .trim(),
          status: status,
          originalStatus: status,
          start: stream.start,
          end: stream.end,
          vimeo_stream_link: stream.vimeo_stream_link || null, // Use the correct field name
          vimeo_download_password: stream.vimeo_download_password || null, // Add this field
          over_due_by: stream.date, // Set to null or compute if you have logic
          password_protected: !!stream.vimeo_download_password, // Already correct
        };
      });

      setStreams(mappedStreams);
      setRowCount(response.data.count);
    } catch (error: unknown) {
      const errorResponse = (error as {
        response?: { status?: number; data?: { detail?: string; message?: string } };
      })?.response;
      console.error("Error fetching streams:", error, errorResponse);
      let errorMessage = "Failed to fetch streams. Please try again.";
      if (errorResponse?.status === 500) {
        errorMessage = "Server error: Unable to retrieve streams.";
        showErrorToast(errorMessage, "fetch-streams-error");
      } else if (errorResponse?.status === 401) {
        errorMessage = "Unauthorized: Please log in again.";
        showErrorToast(errorMessage, "fetch-streams-error");
        dispatch(logout());
        navigate("/login");
      } else if (errorResponse?.data?.detail) {
        errorMessage = errorResponse.data.detail;
        showErrorToast(errorMessage, "fetch-streams-error");
      } else if (errorResponse?.data?.message) {
        errorMessage = errorResponse.data.message;
        showErrorToast(errorMessage, "fetch-streams-error");
      }
    } finally {
      setIsLoading(false);
    }
  }, [appliedSearchTerm, dispatch, navigate, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    fetchStreams();
  }, [fetchStreams]);

  // Debounce for search term
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    if (searchTerm !== appliedSearchTerm) {
      debounceTimer.current = setTimeout(() => {
        setAppliedSearchTerm(searchTerm);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }, 500);
    }
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchTerm, appliedSearchTerm]);

  const handleSearch = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    setAppliedSearchTerm(searchTerm);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns: GridColDef<IStreams>[] = [
    { field: "id", headerName: "Stream ID", width: 80 },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      renderCell: (params) => {
        const title = params.value || "";
        return title.replace(/^Stream for\s*/i, "");
      },
    },
    {
      field: "producer",
      headerName: "Producer",
      flex: 1,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
          <Avatar src="/static/images/avatars/avatar_3.png" />
          <Typography variant="subtitle2">
            {params.row.producer || "Mazvita Ndorochena"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "serviceDate",
      headerName: "Due Date",
      flex: 1,
      renderCell: (params) => params.row.serviceDate || "16 Sept 2025",
    },
    {
      field: "overDueBy",
      headerName: "Overdue",
      flex: 1,
      renderCell: () => "24 hrs",
    },
    {
      field: "link",
      headerName: "Link",
      flex: 1,
      renderCell: (params) => {
        const link = params.row.vimeo_stream_link;
        return link ? (
          <Typography
            variant="subtitle2"
            sx={{
              mt: 2,
              cursor: "pointer",
              color: reduxTheme.primary_colors[500],
            }}
            onClick={(e) => {
              e.stopPropagation();
              window.open(link, "_blank");
            }}
          >
            {link}
          </Typography>
        ) : (
          <Typography sx={{ mt: 2 }}>...</Typography>
        );
      },
    },
    {
      field: "password_protected",
      headerName: "Password Protection",
      flex: 1,
      renderCell: (params) => (
        <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
          {params.row.password_protected ? <Key /> : <KeyOffOutlined />}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      width: 150,
      renderCell: (params) => {
        const hasLink = !!params.row.vimeo_stream_link;
        return hasLink ? (
          <PenPaperIcon
            sx={{
              cursor: "pointer",
              color: "grey.600",
              transition: "0.2s ease",

              "&:hover": {
                color: "primary.main", // ← change to any color you want
                transform: "scale(1.1)", // optional: nice hover effect
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedStreamId(parseInt(params.row.id));
              setModalOpen(true);
            }}
          />
        ) : (
          <Button
            variant="outlined"
            size="small"
            sx={{ borderRadius: 3 }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedStreamId(parseInt(params.row.id));
              setModalOpen(true);
            }}
          >
            Add Link
          </Button>
        );
      },
    },
  ];

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  const handleRowClick = (params: GridRowParams<IStreams>) => {
    navigate(`/stream-management/view/${params.row.id}`);
  };

  const handleSubmitVimeoData = async (_data: {
    streamId: number | "";
    vimeo_stream_link: string;
    password_protected: boolean;
    vimeo_download_password?: string;
  }) => {
    void _data;
    setModalOpen(false);
    await fetchStreams(); // Refresh streams after submission
  };

  return (
    <Box>
      <Box
        sx={{
          backgroundColor: reduxTheme.primary_colors[900],
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          pl: 1,
          width: "30%",
          mb: 4,
          mt: 1,
        }}
      >
        <IconButton onClick={handleSearch}>
          <SearchOutlined />
        </IconButton>
        <InputBase
          placeholder="Search by title, service, or package..."
          sx={{ ml: 1, flex: 1 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearch();
            }
          }}
        />
        <IconButton>
          <FilterListOutlined />
        </IconButton>
      </Box>

      <DataGrid
        rows={streams}
        columns={columns}
        loading={isLoading}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        onRowClick={handleRowClick}
        rowHeight={60}
      />

      <SelectStreamModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmitVimeoData}
        streams={streams}
        title="Add Link"
        initialSelectedStream={selectedStreamId}
      />
    </Box>
  );
};
